import { Controller } from "@hotwired/stimulus";

export default class ProfilePreviewController extends Controller {
  static values = {
    userId: String,
    userName: String,
    shape: { type: String, default: "auto" }
  };

  connect() {
    this.store = globalThis.__profilePreviewStore ||= {};
    // Prevent the next user's profile dropdown from opening after clicking inside the current dropdown
    this.state = globalThis.__profilePreviewState ||= {
      hoverSuppressedUntil: 0
    };

    if (!this.state.modalListenerBound) {
      document.addEventListener("show.bs.modal", () => {
        this.state.modalOpen = true;
        for (const entry of Object.values(globalThis.__profilePreviewStore || {})) {
          entry?.activeController?.hide();
        }
      });
      document.addEventListener("hidden.bs.modal", () => {
        this.state.modalOpen = Boolean(document.querySelector(".modal.show"));
      });
      this.state.modalListenerBound = true;
    }

    if (!this.state.beforeCacheListenerBound) {
      document.addEventListener("turbo:before-cache", () => {
        for (const entry of Object.values(globalThis.__profilePreviewStore || {})) {
          if (!entry?.node) continue;

          entry.node.style.display = "none";
          entry.activeController = null;
        }
      });
      this.state.beforeCacheListenerBound = true;
    }

    this.ensureSharedDropdown();
    this.syncFollowState();

    this._isMouseInside = false;
    this._onMouseEnter = this.onMouseEnter.bind(this);
    this._onMouseMove = this.onMouseMove.bind(this);
    this._onMouseLeave = this.onMouseLeave.bind(this);
    this._outsideClick = this.handleOutsideClick.bind(this);
    this._insideClick = this.clickShow.bind(this);
    this._mobileClick = this.handleMobileClick.bind(this);

    document.addEventListener("click", this._outsideClick);
    this.element.addEventListener("mouseenter", this._onMouseEnter);
    this.element.addEventListener("mousemove", this._onMouseMove);
    this.element.addEventListener("mouseleave", this._onMouseLeave);
    this.element.addEventListener("click", this._mobileClick);

    if (!this.entry.eventsBound) {
      this._dropdownEnter = this.onDropdownEnter.bind(this);
      this._dropdownLeave = this.onDropdownLeave.bind(this);
      this._dropdownClick = this.onDropdownClick.bind(this);

      this.dropdown.addEventListener("mouseenter", this._dropdownEnter);
      this.dropdown.addEventListener("mouseleave", this._dropdownLeave);
      this.dropdown.addEventListener("click", this._dropdownClick);

      this.entry.eventsBound = true;
    }

    if (!this.entry.loaded && !this.entry.loading) {
      this.loadUserDataOnce();
    }

    this._onScroll = this.handleScroll.bind(this);
    globalThis.addEventListener("scroll", this._onScroll, true);
    globalThis.addEventListener("resize", this._onScroll);
  }

  handleScroll() {
    for (const entry of Object.values(this.store)) {
      if (!entry?.node) continue;

      entry.node.style.display = "none";

      if (entry.activeController) {
        entry.activeController.hide();
        entry.activeController = null;
      }
    }
  }

  disconnect() {
    this.clearHideTimer();

    if (this.entry?.activeController === this) {
      this.hide();
    }

    this.element.removeEventListener("mouseenter", this._onMouseEnter);
    this.element.removeEventListener("mousemove", this._onMouseMove);
    this.element.removeEventListener("mouseleave", this._onMouseLeave);
    this.element.removeEventListener("click", this._mobileClick);
    document.removeEventListener("click", this._outsideClick);
    if (this._mobileTapTimer) {
      clearTimeout(this._mobileTapTimer);
      this._mobileTapTimer = null;
    }

    if (
      this.entry?.node &&
      !document.body.contains(this.entry.node)
    ) {
      delete this.store[this.userIdValue];
    }
    globalThis.removeEventListener("scroll", this._onScroll, true);
    globalThis.removeEventListener("resize", this._onScroll);
  }

  isInsideTarget(e) {
    if (!e || typeof e.clientX !== "number" || typeof e.clientY !== "number") {
      return false;
    }
    const images = Array.from(this.element.querySelectorAll("img"));
    const backgroundImage = images.find((img) => {
      const src = img.currentSrc || img.src || "";
      return src.includes("user-profile-bg") || src.includes("profile-bg");
    });

    if (!backgroundImage) {
      const avatar = this.element.querySelector(".main-user-img");
      return avatar ? this.isPointOnVisibleImage(avatar, e.clientX, e.clientY) : false;
    }

    return this.isPointOnVisibleImage(backgroundImage, e.clientX, e.clientY, true);
  }

  isPointOnVisibleImage(image, clientX, clientY, defaultAvatar = false) {
    const rect = image.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;

    if (defaultAvatar) {
      // Include the outer circular border, but exclude the bubble tail.
      const naturalWidth = image.naturalWidth || 230;
      const naturalHeight = image.naturalHeight || 238;
      const framedAvatar = (image.currentSrc || image.src || "").includes("user-profile-bg");
      if (framedAvatar) {
        const radiusX = rect.width / 2;
        const radiusY = rect.height * naturalWidth / naturalHeight / 2;
        return ((clientX - rect.left - radiusX) / radiusX) ** 2 +
          ((clientY - rect.top - radiusY) / radiusY) ** 2 <= 1;
      }
      const scale = Math.min(rect.width / naturalWidth, rect.height / naturalHeight);
      const width = naturalWidth * scale;
      const height = naturalHeight * scale;
      const radius = width / 2;
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + (rect.height - height) / 2 + width / 2;
      return ((clientX - centerX) / radius) ** 2 +
        ((clientY - centerY) / radius) ** 2 <= 1;
    }

    // Use the avatar's oval bounds instead of reading CDN image pixels.
    // Canvas reads require CORS and must never trigger a reload of the frame.
    const x = (clientX - rect.left - rect.width / 2) / (rect.width / 2);
    const y = (clientY - rect.top - rect.height / 2) / (rect.height / 2);
    return x * x + y * y <= 1;
  }

  isElementHovered() {
    return this._isMouseInside === true;
  }

  onMouseEnter(e) {
    if (this.isTouchDevice()) return;

    if (!this.isInsideTarget(e)) {
      this._isMouseInside = false;
      return;
    }

    this._isMouseInside = true;
    this.show();
  }

  onMouseMove(e) {
    if (this.isTouchDevice()) return;

    const inside = this.isInsideTarget(e);

    if (inside) {
      if (this._isMouseInside) {
        this.clearHideTimer();
      } else {
        this._isMouseInside = true;
        this.clearHideTimer();
        this.show();
      }
    } else {
      if (!this._isMouseInside) return;
      this._isMouseInside = false;
      this.scheduleHide();
    }
  }

  onMouseLeave(e) {
    if (this.isTouchDevice()) return;

    this._isMouseInside = false;
    this.hideIfOutside(e);
  }

  ensureSharedDropdown() {
    const key = this.userIdValue;
    const existing = this.store[key];

    if (existing) {
      if (document.body.contains(existing.node)) {
        this.entry = existing;
        this.dropdown = existing.node;
        return;
      }

      delete this.store[key];
    }

    const dropdown = document.createElement("div");

    dropdown.className =
      "dropdown-menu show bg-img-dot rounded-4 text-start bg-sky-blue border-3 border-solid border-black bg-repeat bg-auto py-10 px-10 px-md-20 p-2 text-black w-230px";

    dropdown.style.position = "fixed";
    dropdown.style.display = "none";
    dropdown.style.zIndex = "9999";

    dropdown.innerHTML = `
      <div class="p-3">
        <div class="placeholder-glow">
          <span class="placeholder col-12"></span>
          <span class="placeholder col-8"></span>
          <span class="placeholder col-6"></span>
        </div>
      </div>
    `;

    document.body.appendChild(dropdown);

    this.store[key] = {
      node: dropdown,
      loaded: false,
      loading: false,
      activeController: null,
      eventsBound: false,
      updatedAt: 0,
      followerCount: null,
      followingCount: null
    };

    this.entry = this.store[key];
    this.dropdown = dropdown;
  }

  async loadUserDataOnce() {
    if (this.entry.loading || this.entry.loaded) return;

    this.entry.loading = true;

    try {
      const response = await fetch(
        `/profiles/${this.userNameValue}/user_hover_card`,
        {
          headers: {
            Accept: "text/html"
          }
        }
      );

      const html = await response.text();

      this.dropdown.innerHTML = html;
      this.entry.loaded = true;
      this.readCachedFollowState();
      this.syncFollowState();
    } catch {
      this.dropdown.innerHTML =
        "<div class='p-3'>Failed to load profile</div>";
    } finally {
      this.entry.loading = false;
    }
  }

  show() {
    const activeModal = document.querySelector(".modal.show");
    const triggerModal = this.element.closest(".modal.show");

    if ((this.state.modalOpen || activeModal) && !triggerModal) {
      return;
    }

    if (Date.now() < this.state.hoverSuppressedUntil) {
      return;
    }

    this.clearHideTimer();

    for (const entry of Object.values(this.store)) {
      if (
        entry?.activeController &&
        entry.activeController !== this
      ) {
        entry.activeController.hide();
      }
    }

    if (!document.body.contains(this.dropdown)) {
      delete this.store[this.userIdValue];
      this.ensureSharedDropdown();
    }
    this.syncFollowState();

    this.entry.activeController = this;

    this.position();
    this.dropdown.style.display = "block";

    if (!this.entry.loaded && !this.entry.loading) {
      this.loadUserDataOnce();
    }
  }

  hide() {
    this.dropdown.style.display = "none";

    if (this.entry.activeController === this) {
      this.entry.activeController = null;
    }
  }

  position() {
    const rect = this.element.getBoundingClientRect();

    this.dropdown.style.visibility = "hidden";
    this.dropdown.style.display = "block";

    const dropdownRect = this.dropdown.getBoundingClientRect();
    const viewportHeight = globalThis.innerHeight;
    const viewportWidth = globalThis.innerWidth;
    const gap = 0;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top;

    if (spaceBelow >= dropdownRect.height) {
      top = rect.bottom + gap;
    } else if (spaceAbove >= dropdownRect.height) {
      top = rect.top - dropdownRect.height - gap;
    } else {
      top = spaceBelow > spaceAbove
        ? rect.bottom + gap
        : Math.max(10, rect.top - dropdownRect.height - gap);
    }

    let left = rect.left;

    if (left + dropdownRect.width > viewportWidth) {
      left = viewportWidth - dropdownRect.width - 10;
    }

    if (left < 10) {
      left = 10;
    }

    this.dropdown.style.left = `${left}px`;
    this.dropdown.style.top = `${top}px`;
    this.dropdown.style.visibility = "visible";
  }

  hideIfOutside(e) {
    const related = e.relatedTarget;

    if (
      related &&
      (
        this.element.contains(related) ||
        this.dropdown.contains(related)
      )
    ) {
      return;
    }

    this.scheduleHide();
  }

  onDropdownEnter() {
    this.clearHideTimer();
  }

  onDropdownLeave(e) {
    const related = e.relatedTarget;

    if (
      related &&
      (
        !this.element.contains(related) &&
        !this.dropdown.contains(related)
      )
    ) {
      this.hide();
    }
  }

  onDropdownClick(e) {
    const related = e.target;

    if (related && this.dropdown.contains(related)) {
      this.state.hoverSuppressedUntil = Date.now() + 300;
      this.hide();
    }
  }

  scheduleHide() {
    this.clearHideTimer();

    this.hideTimer = setTimeout(() => {
      if (this.entry.activeController !== this) return;

      const dropdownHovered = this.dropdown?.matches(":hover");
      if (!this.isElementHovered() && !dropdownHovered) {
        this.hide();
      }
    }, 200);
  }

  clearHideTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  clickShow(e) {
    e.preventDefault();
    e.stopPropagation();

    if (
      this.dropdown.style.display === "block" &&
      this.entry.activeController === this
    ) {
      this.hide();
    } else {
      this.show();
    }
  }

  handleOutsideClick(e) {
    const target = e.target;

    if (
      !this.element.contains(target) &&
      !this.dropdown.contains(target)
    ) {
      this.hide();
    }
  }

  isTouchDevice() {
    return (
      "ontouchstart" in globalThis ||
      navigator.maxTouchPoints > 0
    );
  }

  handleMobileClick(e) {
    if (!this.isTouchDevice()) return;

    // Only react when clicking the visible part of the profile image
    if (!this.isInsideTarget(e)) return;

    const now = Date.now();

    // SECOND TAP = DOUBLE TAP
    if (this._mobileLastTap && now - this._mobileLastTap < 350) {
      e.preventDefault();
      e.stopPropagation();

      // Cancel the single-tap redirect
      if (this._mobileTapTimer) {
        clearTimeout(this._mobileTapTimer);
        this._mobileTapTimer = null;
      }
      this._mobileLastTap = 0;
      // Open dropdown
      this.show();

      return;
    }

    e.preventDefault();
    this._mobileLastTap = now;

    // Wait before redirecting so we can detect a second tap
    this._mobileTapTimer = setTimeout(() => {
      this._mobileTapTimer = null;
      this._mobileLastTap = 0;

      const link = this.element.closest("a[href]");

      if (link?.href) {
        globalThis.location.href = link.href;
      }
    }, 350);
  }

  syncFollowState() {
    const currentState = this.getFollowState(this.element);
    const cachedState = this.getFollowState(this.dropdown);

    if (!currentState || !cachedState) return;

    const currentUpdatedAt = currentState.updatedAt;
    const cachedUpdatedAt = cachedState.updatedAt;

    if (currentUpdatedAt > cachedUpdatedAt) {
      this.copyFollowState(
        this.element,
        this.dropdown,
        currentState
      );

      return;
    }

    if (cachedUpdatedAt > currentUpdatedAt) {
      this.copyFollowState(
        this.dropdown,
        this.element,
        cachedState
      );
    }
  }

  getFollowState(element) {
    if (!element) return null;

    const followers = element.querySelector(
      ".hover-card-followers"
    );

    const followings = element.querySelector(
      ".hover-card-followings"
    );

    if (!followers && !followings) return null;

    const followerUpdatedAt = Number(
      followers?.dataset.updatedAt || 0
    );

    const followingUpdatedAt = Number(
      followings?.dataset.updatedAt || 0
    );

    return {
      updatedAt: Math.max(
        followerUpdatedAt,
        followingUpdatedAt
      ),
      followerCount: followers?.textContent?.trim() ?? null,
      followingCount: followings?.textContent?.trim() ?? null
    };
  }

  copyFollowState(source, target, state) {
    if (!source || !target || !state) return;

    const targetFollowers = target.querySelector(
      ".hover-card-followers"
    );

    const targetFollowings = target.querySelector(
      ".hover-card-followings"
    );

    if (targetFollowers && state.followerCount !== null) {
      updateTextIfChanged(
        targetFollowers,
        state.followerCount
      );

      targetFollowers.dataset.updatedAt =
        String(state.updatedAt);
    }

    if (targetFollowings && state.followingCount !== null) {
      updateTextIfChanged(
        targetFollowings,
        state.followingCount
      );

      targetFollowings.dataset.updatedAt =
        String(state.updatedAt);
    }

    if (target === this.dropdown) {
      this.entry.updatedAt = state.updatedAt;
      this.entry.followerCount = state.followerCount;
      this.entry.followingCount = state.followingCount;
    }
  }

  readCachedFollowState() {
    const state = this.getFollowState(this.dropdown);

    if (!state) return;

    this.entry.updatedAt = state.updatedAt;
    this.entry.followerCount = state.followerCount;
    this.entry.followingCount = state.followingCount;
  }
}

function updateTextIfChanged(element, value) {
  const newValue = String(value);

  if (element.textContent !== newValue) {
    element.textContent = newValue;
  }
}
